import React, { createContext, useCallback, ReactNode } from 'react';
import { useGetFeaturesQuery } from '../queries/useGetFeaturesQuery';
import { FeatureName, FeaturesResponse } from '../interfaces/features';

interface FeaturesContextType {
  loading: boolean;
  error: boolean;
  features: FeaturesResponse | undefined;
  checkFeaturesEnabled: (featureNames: Array<FeatureName>) => boolean;
  featuresLoaded: boolean;
}

export const FeaturesContext = createContext<FeaturesContextType>({
  loading: true,
  error: false,
  features: [],
  checkFeaturesEnabled: () => false,
  featuresLoaded: false,
});

export const FeaturesProvider = ({
  children,
}: {
  children: ReactNode;
}): JSX.Element => {
  const {
    data: featuresData,
    isLoading: isFeaturesLoading,
    isError: featuresError,
  } = useGetFeaturesQuery();

  const checkFeaturesEnabled = useCallback(
    (featureNames: Array<FeatureName>) =>
      featureNames.every(featureName =>
        featuresData?.some(
          feature => feature.FeatureName === featureName && feature.IsEnabled,
        ),
      ),
    [featuresData],
  );

  return (
    <FeaturesContext.Provider
      value={{
        features: featuresData,
        loading: isFeaturesLoading,
        error: featuresError,
        checkFeaturesEnabled,
        featuresLoaded: !isFeaturesLoading && !!featuresData,
      }}>
      {children}
    </FeaturesContext.Provider>
  );
};
